import { motion, AnimatePresence } from "framer-motion";
import { Share2, X } from "lucide-react";
import { useRef, useState } from "react";

/**
 * MilestoneSharePanel
 * 
 * A calm, minimal follow-up panel that appears AFTER a milestone celebration
 * has fully completed. Offers optional sharing without pressure.
 * 
 * Tone: Reflective, optional, quiet
 */

const milestoneData = {
  10: {
    title: "Nice Start",
    subtitle: "10 tasks completed",
    message: "You're finding your rhythm."
  },
  25: {
    title: "Quarter Mark",
    subtitle: "25 tasks completed", 
    message: "A solid quarter. Keep the thread going."
  },
  50: {
    title: "Halfway There",
    subtitle: "50 tasks completed",
    message: "Real momentum now."
  },
  100: {
    title: "Full Cycle",
    subtitle: "100 tasks completed",
    message: "You completed a full cycle."
  }
};

// Generate a shareable card as data URL
const generateShareCard = async (milestone) => {
  const data = milestoneData[milestone] || milestoneData[10];
  
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');
  
  // Background - warm cream
  ctx.fillStyle = '#FFFCF9';
  ctx.fillRect(0, 0, 600, 400);
  
  // Subtle border
  ctx.strokeStyle = 'rgba(0,0,0,0.06)';
  ctx.lineWidth = 2;
  ctx.strokeRect(20, 20, 560, 360);
  
  // Milestone number - large, subtle
  ctx.font = 'bold 72px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = 'rgba(245, 130, 39, 0.15)';
  ctx.textAlign = 'center';
  ctx.fillText(milestone.toString(), 300, 140);
  
  // Title
  ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#2D2D2D';
  ctx.fillText(data.title, 300, 200);
  
  // Subtitle
  ctx.font = '16px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#7A7A7A';
  ctx.fillText(data.subtitle, 300, 235);
  
  // Message
  ctx.font = 'italic 14px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#9A9A9A';
  ctx.fillText(data.message, 300, 275);
  
  // Branding - subtle
  ctx.font = '12px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#C4C4C4';
  ctx.fillText('Where Was I', 300, 350);
  
  return canvas.toDataURL('image/png');
};

export const MilestoneSharePanel = ({ isOpen, onClose, milestone }) => {
  const [isSharing, setIsSharing] = useState(false);
  const data = milestoneData[milestone] || milestoneData[10];

  const handleShare = async () => {
    setIsSharing(true);
    
    try {
      const imageDataUrl = await generateShareCard(milestone);
      
      // Convert data URL to blob for sharing
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `milestone-${milestone}.png`, { type: 'image/png' });
      
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${data.title} - ${data.subtitle}`,
          text: 'Back on track. One task at a time.',
          files: [file]
        });
      } else if (navigator.share) {
        // Fallback without image
        await navigator.share({
          title: `${data.title} - ${data.subtitle}`,
          text: `${data.message}\n\nBack on track. One task at a time.\n\n— Where Was I`
        });
      } else {
        // Desktop fallback - copy text
        const text = `${data.title}\n${data.subtitle}\n\n${data.message}\n\nBack on track. One task at a time.\n\n— Where Was I`;
        await navigator.clipboard.writeText(text);
        alert('Copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    } finally {
      setIsSharing(false);
      onClose();
    }
  };

  const handleKeepGoing = () => {
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed bottom-24 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-50"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.98 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div 
            className="bg-white/95 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-stone-100 mx-auto sm:mx-0"
            style={{ maxWidth: '320px' }}
          >
            {/* Minimal content */}
            <div className="flex items-center justify-between gap-6">
              {/* Share option */}
              <button
                onClick={handleShare}
                disabled={isSharing}
                className="flex items-center gap-2 text-[#0CBCBF] hover:text-[#0AA8B3] transition-colors disabled:opacity-50"
              >
                <Share2 size={16} />
                <span className="text-sm font-medium">
                  {isSharing ? 'Sharing...' : 'Share this moment'}
                </span>
              </button>

              {/* Divider */}
              <div className="w-px h-5 bg-stone-200" />

              {/* Keep going option */}
              <button
                onClick={handleKeepGoing}
                className="text-sm text-[#71717A] hover:text-[#52525B] font-medium transition-colors"
              >
                Keep going
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MilestoneSharePanel;
